import styled, { css } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const STORY_CLOSE_SIZE = 42;

const STORY_VIEWER_Z_INDEX = 5000;

export const StoryViewerOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: ${STORY_VIEWER_Z_INDEX};
	display: ${(props) => (props.$open ? 'flex' : 'none')};
	align-items: center;
	justify-content: center;
	gap: clamp(22px, 4vw, 54px);
	padding: 22px;
	background: rgba(0, 0, 0, 0.96);
	backdrop-filter: blur(18px);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0;
		gap: 0;
		align-items: stretch;
		justify-content: stretch;
		height: var(--app-height, 100dvh);
		min-height: var(--app-height, 100dvh);
		max-height: var(--app-height, 100dvh);
	}
`;

const CAROUSEL_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
const CAROUSEL_DURATION = '360ms';
const PEEK_SHIFT =
	'calc(50% + clamp(22px, 4vw, 54px) + min(95px, 7vw))';
const PEEK_SCALE = 0.452;

const carouselCentered = css`
	transform: translate3d(0, 0, 0) scale(1);
	opacity: 1;
	filter: none;
	z-index: 2;
`;

const carouselLeavingNextStart = css`
	transform: translate3d(0, 0, 0) scale(1);
	opacity: 1;
	filter: none;
	z-index: 2;
`;

const carouselLeavingNextEnd = css`
	transform: translate3d(calc(-1 * ${PEEK_SHIFT}), 0, 0) scale(${PEEK_SCALE});
	opacity: 0.62;
	filter: saturate(0.85) brightness(0.82);
	z-index: 1;
`;

const carouselEnteringNextStart = css`
	transform: translate3d(${PEEK_SHIFT}, 0, 0) scale(${PEEK_SCALE});
	opacity: 0.62;
	filter: saturate(0.85) brightness(0.82);
	z-index: 1;
`;

const carouselEnteringNextEnd = css`
	transform: translate3d(0, 0, 0) scale(1);
	opacity: 1;
	filter: none;
	z-index: 2;
`;

const carouselLeavingPrevStart = css`
	transform: translate3d(0, 0, 0) scale(1);
	opacity: 1;
	filter: none;
	z-index: 2;
`;

const carouselLeavingPrevEnd = css`
	transform: translate3d(${PEEK_SHIFT}, 0, 0) scale(${PEEK_SCALE});
	opacity: 0.62;
	filter: saturate(0.85) brightness(0.82);
	z-index: 1;
`;

const carouselEnteringPrevStart = css`
	transform: translate3d(calc(-1 * ${PEEK_SHIFT}), 0, 0) scale(${PEEK_SCALE});
	opacity: 0.62;
	filter: saturate(0.85) brightness(0.82);
	z-index: 1;
`;

const carouselEnteringPrevEnd = css`
	transform: translate3d(0, 0, 0) scale(1);
	opacity: 1;
	filter: none;
	z-index: 2;
`;

export const StoryCenterWrap = styled.div`
	position: relative;
	width: min(420px, 100%);
	height: min(760px, calc(100dvh - 44px));
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		flex: 1 1 auto;
		width: 100%;
		height: 100%;
		min-height: 0;
		max-height: var(--app-height, 100dvh);
	}
`;

export const StoryCarouselStage = styled.div`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
`;

export const StoryCarouselFrame = styled.div`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	transform-origin: center center;
	pointer-events: none;
	backface-visibility: hidden;
	will-change: transform, opacity, filter;
	transition:
		transform ${CAROUSEL_DURATION} ${CAROUSEL_EASE},
		opacity ${CAROUSEL_DURATION} ${CAROUSEL_EASE},
		filter ${CAROUSEL_DURATION} ${CAROUSEL_EASE};

	article {
		width: 100%;
		height: 100%;
		max-width: none;
		flex-shrink: 0;
	}

	${(props) =>
		props.$role === 'center' &&
		css`
			${carouselCentered}
			pointer-events: auto;
		`}

	${(props) =>
		props.$role === 'leaving' &&
		props.$direction === 1 &&
		!props.$animating &&
		carouselLeavingNextStart}

	${(props) =>
		props.$role === 'leaving' &&
		props.$direction === 1 &&
		props.$animating &&
		carouselLeavingNextEnd}

	${(props) =>
		props.$role === 'entering' &&
		props.$direction === 1 &&
		!props.$animating &&
		carouselEnteringNextStart}

	${(props) =>
		props.$role === 'entering' &&
		props.$direction === 1 &&
		props.$animating &&
		carouselEnteringNextEnd}

	${(props) =>
		props.$role === 'leaving' &&
		props.$direction === -1 &&
		!props.$animating &&
		carouselLeavingPrevStart}

	${(props) =>
		props.$role === 'leaving' &&
		props.$direction === -1 &&
		props.$animating &&
		carouselLeavingPrevEnd}

	${(props) =>
		props.$role === 'entering' &&
		props.$direction === -1 &&
		!props.$animating &&
		carouselEnteringPrevStart}

	${(props) =>
		props.$role === 'entering' &&
		props.$direction === -1 &&
		props.$animating &&
		carouselEnteringPrevEnd}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		${(props) =>
			props.$role === 'leaving' &&
			props.$direction === 1 &&
			props.$animating &&
			css`
				transform: translate3d(-18%, 0, 0) scale(0.94);
				opacity: 0.28;
				filter: none;
			`}

		${(props) =>
			props.$role === 'entering' &&
			props.$direction === 1 &&
			!props.$animating &&
			css`
				transform: translate3d(18%, 0, 0) scale(0.94);
				opacity: 0.28;
				filter: none;
			`}

		${(props) =>
			props.$role === 'leaving' &&
			props.$direction === -1 &&
			props.$animating &&
			css`
				transform: translate3d(18%, 0, 0) scale(0.94);
				opacity: 0.28;
				filter: none;
			`}

		${(props) =>
			props.$role === 'entering' &&
			props.$direction === -1 &&
			!props.$animating &&
			css`
				transform: translate3d(-18%, 0, 0) scale(0.94);
				opacity: 0.28;
				filter: none;
			`}
	}
`;

export const StoryFrame = styled.article`
	position: relative;
	width: min(420px, 100%);
	height: min(760px, calc(100dvh - 44px));
	overflow: hidden;
	border-radius: 18px;
	background: #050208;
	box-shadow:
		0 0 42px rgba(176, 38, 255, 0.38),
		0 28px 80px rgba(0, 0, 0, 0.74);

	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.4),
			transparent 34%,
			rgba(0, 0, 0, 0.82)
		);
		pointer-events: none;
		z-index: 1;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		height: 100%;
		min-height: var(--app-height, 100dvh);
		max-height: var(--app-height, 100dvh);
		border-radius: 0;
		box-shadow: none;
	}
`;

export const StoryMedia = styled.div`
	position: absolute;
	inset: 0;
	z-index: 0;
	overflow: hidden;
	background: #050208;

	img,
	video {
		position: absolute;
		inset: 0;
		z-index: 2;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center center;
		display: block;
		user-select: none;
		-webkit-user-select: none;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;
		transform: translateZ(0);
		backface-visibility: hidden;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		img,
		video {
			min-width: 100%;
			min-height: 100%;
		}
	}
`;

export const StoryMediaSkeleton = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	pointer-events: none;
`;

export const StoryPeekSkeleton = styled.div`
	position: absolute;
	inset: 0;
`;

export const StoryHeaderAvatarWrap = styled.div`
	width: 38px;
	height: 38px;
	border-radius: 50%;
	overflow: hidden;
	border: 2px solid rgba(255, 255, 255, 0.7);
	position: relative;

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
		position: relative;
		z-index: 1;
	}

	> div {
		position: absolute;
		inset: 0;
		z-index: 0;
	}
`;

export const StoryProgress = styled.div`
	position: absolute;
	top: 14px;
	left: 14px;
	right: 14px;
	height: 3px;
	display: flex;
	gap: 4px;
	z-index: 2;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		top: max(12px, env(safe-area-inset-top, 0px));
		left: max(12px, env(safe-area-inset-left, 0px));
		right: max(12px, env(safe-area-inset-right, 0px));
	}
`;

export const ProgressSegment = styled.span`
	flex: 1;
	display: block;
	height: 100%;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.24);
	overflow: hidden;
`;

export const ProgressFill = styled.i`
	display: block;
	width: ${(props) => props.$width ?? 0}%;
	min-width: 0;
	height: 100%;
	background: linear-gradient(90deg, ${THEME.pink}, ${THEME.gold});
	will-change: width;
`;

export const StoryHeader = styled.div`
	position: absolute;
	top: 28px;
	left: 18px;
	right: 18px;
	z-index: 3;
	display: grid;
	grid-template-columns: 42px minmax(0, 1fr) auto;
	gap: 10px;
	align-items: center;
	color: #fff;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		top: max(30px, calc(env(safe-area-inset-top, 0px) + 18px));
		left: max(12px, env(safe-area-inset-left, 0px));
		right: max(12px, env(safe-area-inset-right, 0px));
		grid-template-columns: 38px minmax(0, 1fr) auto 34px;
		gap: 8px;
	}
`;

export const StoryAvatar = styled.img`
	width: 38px;
	height: 38px;
	border-radius: 50%;
	border: 2px solid rgba(255, 255, 255, 0.7);
	object-fit: cover;
`;

export const StoryMeta = styled.div`
	min-width: 0;

	strong,
	span {
		display: inline-block;
		font-size: 0.94rem;
		text-shadow: 0 2px 12px rgba(0, 0, 0, 0.72);
	}

	span {
		margin-left: 8px;
		color: rgba(255, 255, 255, 0.72);
	}
`;

export const StoryPlayToggle = styled.button`
	width: 34px;
	height: 34px;
	border: 0;
	border-radius: 0;
	background: transparent;
	color: #fff;
	display: grid;
	place-items: center;
	padding: 0;
	cursor: pointer;
	filter: drop-shadow(0 2px 10px rgba(0, 0, 0, 0.75));

	span {
		display: block;
		width: 18px;
		height: 20px;
	}

	${(props) =>
		props.$paused &&
		css`
			span {
				background:
					linear-gradient(#fff, #fff) left center / 6px 20px no-repeat,
					linear-gradient(#fff, #fff) right center / 6px 20px no-repeat;
			}
		`}

	${(props) =>
		!props.$paused &&
		css`
			span {
				width: 0;
				height: 0;
				border-top: 10px solid transparent;
				border-bottom: 10px solid transparent;
				border-left: 16px solid #fff;
			}
		`}
`;

export const StoryZone = styled.button`
	position: absolute;
	z-index: 2;
	top: 0;
	bottom: 0;
	width: 50%;
	border: 0;
	background: transparent;
	cursor: pointer;

	${(props) =>
		props.$side === 'prev' &&
		css`
			left: 0;
		`}
	${(props) =>
		props.$side === 'next' &&
		css`
			right: 0;
		`}
`;

export const StoryClose = styled.button`
	position: fixed;
	top: 20px;
	right: 22px;
	z-index: ${STORY_VIEWER_Z_INDEX + 1};
	width: ${STORY_CLOSE_SIZE}px;
	height: ${STORY_CLOSE_SIZE}px;
	border: 0;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.08);
	color: #fff;
	font-size: 2rem;
	line-height: 1;
	cursor: pointer;
	display: grid;
	place-items: center;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: none;
	}
`;

export const StoryHeaderClose = styled.button`
	display: none;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: grid;
		place-items: center;
		width: 34px;
		height: 34px;
		border: 0;
		border-radius: 50%;
		background: rgba(255, 255, 255, 0.08);
		color: #fff;
		font-size: 1.65rem;
		line-height: 1;
		padding: 0;
		cursor: pointer;
		justify-self: end;
	}
`;

export const StoryPeek = styled.button`
	width: min(190px, 14vw);
	height: min(340px, 48vh);
	border: 0;
	border-radius: 8px;
	overflow: hidden;
	padding: 0;
	background: #111;
	opacity: ${(props) => (props.$hidden ? 0 : 0.62)};
	filter: saturate(0.85) brightness(0.82);
	box-shadow: 0 16px 42px rgba(0, 0, 0, 0.48);
	cursor: pointer;
	pointer-events: ${(props) => (props.$hidden ? 'none' : 'auto')};
	transition: opacity ${CAROUSEL_DURATION} ${CAROUSEL_EASE};

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	&:hover {
		opacity: 0.86;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		display: none;
	}
`;

export const StoryReply = styled.form`
	position: absolute;
	left: 18px;
	right: 18px;
	bottom: 18px;
	z-index: 3;
	min-height: 46px;
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	gap: 10px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		left: max(12px, env(safe-area-inset-left, 0px));
		right: max(12px, env(safe-area-inset-right, 0px));
		bottom: max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px));
	}
`;

export const StoryReplyInput = styled.input`
	min-width: 0;
	width: 100%;
	min-height: 46px;
	border: 1px solid rgba(255, 255, 255, 0.4);
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.34);
	color: #fff;
	outline: 0;
	padding: 0 16px;
	backdrop-filter: blur(12px);
	font-family: inherit;
	font-size: 0.9rem;

	&::placeholder {
		color: rgba(255, 255, 255, 0.72);
	}
`;

export const StoryReplyButton = styled.button`
	min-height: 46px;
	border: 0;
	border-radius: 999px;
	padding: 0 16px;
	color: #fff;
	font-weight: 800;
	background: linear-gradient(135deg, ${THEME.violet}, ${THEME.pink});
	box-shadow: 0 0 18px rgba(255, 77, 242, 0.32);
	cursor: pointer;
	font-family: inherit;
`;
